/* eslint-disable consistent-return */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import noop from 'lodash/noop';
import isEmpty from 'lodash/isEmpty';
import { useDispatch, useSelector } from 'react-redux';
import * as getTestCredentials from '../../healthboard/requests/network-test-credentials-req';
import {
  selectNetworkTestCredentials,
  selectNetworkTestCredentialsPending,
} from '../../healthboard/selectors';

const TEST_DURATION_MS = 10 * 1000;
const TOTAL_DURATION_SEC = TEST_DURATION_MS / 1000 + 3;
const getSessionInfo = config => {
  if (!config || isEmpty(config)) return null;

  return {
    apiKey: config.apiKey,
    token: config.token,
    sessionId: config.room,
  };
};

export function useTestConnection({ userId }) {
  const dispatch = useDispatch();
  const [testOpened, setOpenedTest] = useState(false);
  const [finished, setFinished] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const otNetworkTest = useRef(null);
  const intervalRef = useRef(null);
  const connectionProgress = useRef(0);

  const loading = useSelector(selectNetworkTestCredentialsPending);
  const testCredentials = useSelector(selectNetworkTestCredentials);

  useEffect(() => () => clearInterval(intervalRef.current), []);

  useEffect(() => {
    if (testCredentials) {
      const credentials = getSessionInfo(testCredentials);
      if (credentials) {
        startTesting(credentials);
      }
    }
  }, [testCredentials]);

  const reset = () => {
    setFinished(false);
    setProgress(0);
  };

  const trackConnectionProgress = useCallback(() => {
    intervalRef.current = setInterval(
      () =>
        setProgress(prevProgress => {
          const newProgress = prevProgress + 1;
          connectionProgress.current = newProgress;
          return newProgress;
        }),
      1000,
    );
  }, []);

  const testConnection = async () => {
    trackConnectionProgress();
    await otNetworkTest.current.testConnectivity();
    clearInterval(intervalRef.current);
  };

  const testQuality = async () => {
    const delta = Math.abs(TOTAL_DURATION_SEC - connectionProgress.current);
    const k = delta / (TEST_DURATION_MS / 1000);

    const res = await otNetworkTest.current.testQuality(stats => {
      setProgress(prevProgress => prevProgress + k);
      noop(stats);
    });
    setResult(res);
    setFinished(true);
  };

  const fetchTestCredentials = useCallback(() => {
    setOpenedTest(true);
    reset();
    dispatch(getTestCredentials.actions.send({ id: userId }));
  }, [userId]);

  const startTesting = useCallback(async credentials => {
    try {
      const options = { timeout: TEST_DURATION_MS };
      otNetworkTest.current = new OpenTokNetworkConnectivity.default(
        OT,
        credentials,
        options,
      );
      await testConnection();
      await testQuality();
    } catch (error) {
      console.log('ERROR DURING TEST CONNECTION', error);
      setOpenedTest(false);
      reset();
    }
  }, []);

  const stopTesting = useCallback(() => {
    setOpenedTest(false);
    reset();
    if (otNetworkTest.current) {
      otNetworkTest.current.stop();
      otNetworkTest.current = null;
    }
  }, []);

  const buttonProps = useMemo(() => {
    if (!testOpened) {
      return {
        title: 'virtualVisit.test.test',
        onClick: fetchTestCredentials,
        color: 'primary',
      };
    }

    if (!finished) {
      return {
        title: 'virtualVisit.test.cancel',
        onClick: stopTesting,
        color: 'danger-white',
      };
    }

    return {
      title: 'virtualVisit.test.retest',
      onClick: fetchTestCredentials,
      color: 'accent',
    };
  }, [testOpened, finished]);

  return {
    startTesting,
    stopTesting,
    buttonProps,
    disabled: loading,
    progress: (progress * 100) / TOTAL_DURATION_SEC,
    testOpened,
    finished,
    result,
  };
}



// WEBPACK FOOTER //
// ./src/redux/opentok/hooks/useTestConnection.js